import { titleize } from '@common/helpers/strings';
import {
  getBreadCrumbsForLocation,
  getStateAbbr,
  isCanadaPath,
  isInternationalPath,
} from '@common/helpers/url';
import EntityTile from '@react/card';
import {
  getEventProps,
  getTypeformProps,
} from '@react/cta/helpers/getEventProps';
import { getIsAllowed } from '@react/data-hub/helpers/getIsAllowed';
import { AccordianWrapper as InlineGwizzyAccordian } from '@react/gwizzy/components/InlineGwizzyAccordian';
import SearchPagination from '@react/search/components/SearchPagination';
import Filters from '@react/search/Filters';
import FilterButton from '@react/search/Filters/FilterButton';
import { GetSeoContent, getTocSeoLabel } from '@react/search/helpers';
import { usePrefetch } from '@react/services/api';
import BreadCrumb from '@react/shared/components/BreadCrumb';
import Header from '@react/shared/components/Header/Header.container';
import useDimensions from '@react/shared/hooks/useDimensions';
import Heading from '@react/system/Heading';
import { DEFAULT_PAGE_SIZE } from '@sly/core/constants/filters';
import { LIST, MAP, SHOW_OPTIONS } from '@sly/core/constants/search';
import { DEFAULT_CTA_OBJECTS } from '@sly/core/constants/strapi';
import { SEARCH_PAGE_GWIZZY_ENTRY } from '@sly/core/src/constants/assessment';
import { SearchPageSeo } from '@sly/frontend/common/helpers/html_headers';
import cx from 'classnames';
import { useRouter } from 'next/router';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { List, Map } from 'sly/icons';
import { useBreakpoint } from 'sly/services/breakpoint';
import { useEvents } from 'sly/services/events';
import withHydration from 'sly/services/partialHydration';
import Button from 'sly/system/Button';

import Curated, { CuratedIntro } from '../Curated';
import { SearchMap } from '../SearchMap';

const ExploreContainer = withHydration(
  /* #__LOADABLE__ */ () =>
    import(
      /* webpackChunkName: "chunkExploreContainer" */ '@react/search/components/ExploreContainer'
    )
);

const Footer = withHydration(
  /* #__LOADABLE__ */ () =>
    import(
      /* webpackChunkName: "chunkFooter" */ '@react/shared/components/Footer'
    )
);

const SearchPageInsightsWrapper = withHydration(() =>
  import(
    /* webpackChunkName: "chunkSearchPageInsightsWrapper" */ '@react/data-hub/components/SearchPageInsightsWrapper'
  )
);

const DatahubContextProvider = withHydration(() =>
  import(
    /* webpackChunkName: "chunkDatahubContextProvider" */ '@react/data-hub/context/DatahubContextProvider'
  )
);

export const TemplateContent = ({ children }) => (
  <>
    <div
      className='my-0 mx-auto w-full px-m py-0 md:w-[32.5rem] md:p-0 lg:w-[42.5rem]'
      data-tp-id='gen-div-c47d7a69-e614-41b0-b05e-5a8b346ce08e'
    >
      {children}
    </div>
  </>
);

const Search = ({
  currentFilters,
  onFilterChange,
  onClearFilters,
  entities,
  meta,
  pagination,
  location,
  hasFinished,
  experiments = {},
  geoGuide,
  showHeaderPhNo,
  chartSvgs,
}) => {
  const {
    ref: headerRef,
    dimensions: { height: headerHeight = 80 },
  } = useDimensions();
  const breakpoint = useBreakpoint();

  const query = useRouter().query;

  const {
    ref: filtersRef,
    dimensions: { height: filtersHeight = 84 },
  } = useDimensions();

  const [show, setShow] = useState(LIST);
  const [hoveredCommunity, setHoveredCommunity] = useState(null);
  const { sendCommunityListViewedEvent } = useEvents();

  const {
    ctaBoxDescription,
    ctaBoxHeading,
    ctaButtonText,
    ctaBoxAfterSearch = DEFAULT_CTA_OBJECTS.searchAssessmentWizard,
    isInlineGwizzyOpen,
    isSearchInsightsEnabled,
  } = experiments;

  let listSize = meta['filtered-count'];

  const nextShow = useMemo(() => {
    const showOptions = Object.keys(SHOW_OPTIONS);
    const current = showOptions.indexOf(show);
    return showOptions[Number(!current)];
  }, [show]);

  const toggleShow = useCallback(() => {
    setShow(nextShow);
  }, [nextShow]);

  const page = meta['page-number'] || 0;
  const cursor = DEFAULT_PAGE_SIZE * page + 1;
  const { city, state, toc, county } = currentFilters;
  const stateStr = getStateAbbr(titleize(state));
  const tocLabel = getTocSeoLabel(toc);
  const { requestInfo: requestInfoAgents } = usePrefetch('getAgents', {
    'filter[address]': `${city},${state}`,
    'page-size': 3,
  });
  const reviewer = requestInfoAgents?.normalized?.[0];

  let locationStr = '';
  if (county) {
    locationStr = `${titleize(county)}, ${getStateAbbr(state)}`;
  } else if (city) {
    locationStr = `${titleize(city)}, ${getStateAbbr(state)}`;
  } else {
    locationStr = `${titleize(state)}`;
  }

  listSize = listSize ?? 0;

  let numResultsStr = listSize < 20 ? `The ${listSize} Best` : 'The 20 Best';

  if (listSize === 0) {
    numResultsStr = 'No Results for';
  }

  const title =
    page > 0
      ? `${tocLabel} in ${locationStr}`
      : `${numResultsStr} ${tocLabel} in ${locationStr} `;
  const isInternational = isInternationalPath(location.pathname);
  const isCanada = isCanadaPath(location.pathname);
  const getPriceRange = (geoGuide) => {
    return {
      high:
        geoGuide?.info?.pricingStats?.estimatedPrices?.large
          ?.estimatedAverage || 0,
      low:
        geoGuide?.info?.pricingStats?.estimatedPrices?.small
          ?.estimatedAverage || 0,
    };
  };

  useEffect(() => {
    sendCommunityListViewedEvent(
      entities,
      city ? titleize(city) : undefined,
      stateStr,
      'search',
      tocLabel
    );
  }, []);

  const isSearchInsightsAllowed = getIsAllowed({
    toc: toc,
    city: city,
  });

  const isShowingSearchInsights =
    isSearchInsightsEnabled && isSearchInsightsAllowed;

  const hideMap = true;

  return (
    <>
      <SearchPageSeo
        city={currentFilters.city}
        state={currentFilters.state}
        toc={currentFilters.toc}
        county={currentFilters.county}
        latitude={currentFilters.latitude}
        longitude={currentFilters.longitude}
        pagination={pagination}
        location={location}
        communityList={entities}
        listSize={listSize}
        reviewer={reviewer}
      />
      <div className='flex flex-col'>
        <header className='sticky top-0 z-[1000] w-full' ref={headerRef}>
          <Header showHeaderPhNo={showHeaderPhNo} showHeaderAgentPhNo />
        </header>

        <div
          data-tp-id='gen-div-a2f6204f-a790-4dfc-8182-aa0f1894c92f'
          className={cx(
            show === LIST
              ? 'flex flex-col lg:mx-auto lg:max-w-[1032px]'
              : show === MAP && '!m-0 lg:w-[100vw]'
          )}
        >
          {/* Heading Section Start */}
          <div
            className={cx(
              'gridAreaHeading',
              show === LIST ? 'pt-m md:pt-xl' : 'pt-0',
              'px-l',
              'z-[100]'
            )}
            data-tp-id='gen-div-2d424061-615f-4143-9867-4d13a2643f95'
          >
            <div
              className={cx(show === LIST ? 'block' : 'hidden')}
              data-tp-id='gen-div-e0bf8a1c-3bc4-454c-8c1b-ffce6d21b803'
            >
              <Heading
                className='font-t-l p-xs'
                as='h1'
                font='title-xlarge'
                data-testid='search-heading'
              >
                {title}
              </Heading>
              {(city || county) &&
                state &&
                toc &&
                pagination?.current === 0 && (
                  <div data-tp-id='gen-div-3f87f0ba-54a7-414a-bca0-5de720f84929'>
                    {GetSeoContent({
                      toc,
                      city: county ? titleize(county) : titleize(city),
                      state: getStateAbbr(state),
                      totalEntities: pagination?.count,
                      priceRange: getPriceRange(geoGuide),
                    })}
                  </div>
                )}
            </div>
          </div>
          {/* Heading Section End */}
          {/* Filters Section Start */}
          <Filters
            ref={filtersRef}
            currentFilters={currentFilters}
            onFilterChange={onFilterChange}
            onClearFilters={onClearFilters}
            showTOC={!isInternational || isCanada}
            query={`${location.pathname}${location.search}`}
          >
            <div
              className={cx('ml-auto flex')}
              data-tp-id='gen-div-b3d051cc-62ad-47ca-95c0-0ca02c49da60'
            >
              <FilterButton
                eventProps={{
                  text: nextShow,
                  cta: nextShow === MAP ? 'Toggle Map' : 'Toggle List',
                  location: 'search panel',
                }}
                onClick={toggleShow}
              >
                {nextShow === 'map' && (
                  <>
                    <Map />
                    &nbsp;{SHOW_OPTIONS.map}
                  </>
                )}
                {nextShow === 'list' && (
                  <>
                    <List />
                    &nbsp;{SHOW_OPTIONS.list}
                  </>
                )}
              </FilterButton>
            </div>
          </Filters>
          {/* Filters Section End */}
          {hasFinished && !listSize && (
            <div
              className='gridAreaNoResults z-50 mt-m p-l'
              data-tp-id='gen-div-f9a45962-cabd-47d0-8a06-0ba232e22e78'
            >
              <Heading
                font='title-small'
                className='font-t-s-azo'
                data-tp-id='gen-Heading-03b8fa7b-6b19-472b-beb9-a70bd255293d'
              >
                No results
              </Heading>
              <div
                className='mb-m'
                data-tp-id='gen-div-bcb9718b-aa5e-4712-bad0-2f571b34dcb8'
              >
                Try removing some filters or zooming out on the map to find more
                communities.
              </div>
              <Button
                variant='underlined'
                aria-label='Clear all filters'
                onClick={() => onClearFilters()}
                className='font-normal no-underline hover:underline'
                data-tp-id='gen-Link-43aed640-196a-42b1-94a5-f5e2d483cc54'
              >
                Clear all filters
              </Button>
            </div>
          )}
          <div
            className={cx(
              'gridAreaList hidden pb-xl pt-l',
              show === LIST ? '!block' : 'hidden',
              'md:pb-[unset]'
            )}
          >
            <div className='mx-4 md:mx-6'>
              <span className='font-t-xs-azo'>
                {pagination?.count} communities
              </span>{' '}
              near {titleize(city || county)}, {titleize(state)}
            </div>
            <div id='searchTilesContainer'>
              {entities.map((entity, i) => (
                <Fragment key={entity.id}>
                  <EntityTile
                    setHoveredEntity={setHoveredCommunity}
                    eventProps={{
                      cta: 'View community',
                      location: 'search list',
                      listPosition: cursor + i,
                      query: `${location.pathname}${location.search}`,
                      page,
                    }}
                    intent='search'
                    query={`${location.pathname}${location.search}`}
                    entity={entity}
                    preloadFirstImage={i <= 2}
                  />
                  {!isInternational &&
                    city &&
                    ((entities.length <= 4 && i === entities.length - 1) ||
                      (entities.length > 4 && i === 4)) && (
                      <div
                        className='mx-l mb-l mt-0'
                        data-tp-id='gen-div-154c4060-744f-4a79-96fa-562d78f103da'
                      >
                        <div className='my-6'>
                          <InlineGwizzyAccordian
                            title='Get personalized community recommendations'
                            sectionProps={{ isOpen: !!isInlineGwizzyOpen }}
                            eventProps={{
                              location: 'inline conversion wizard search page',
                              state,
                              city,
                              isOpenByDefault: !!isInlineGwizzyOpen,
                              cta: 'Inline Gwizzy',
                            }}
                            mode={{ entry: SEARCH_PAGE_GWIZZY_ENTRY }}
                          />
                        </div>
                      </div>
                    )}
                </Fragment>
              ))}
            </div>

            <SearchPagination pagination={pagination}>
              <BreadCrumb
                className='md:block'
                items={getBreadCrumbsForLocation(
                  currentFilters,
                  false,
                  'search-breadcrumb'
                )}
              />
            </SearchPagination>

            {pagination?.current === 1 &&
              geoGuide?.info?.curated &&
              !(Object.keys(currentFilters).length > 3) && (
                <Curated
                  city={city}
                  state={state}
                  toc={toc}
                  geoGuide={geoGuide}
                  communitiesCount={pagination?.count}
                />
              )}

            {isShowingSearchInsights && (
              <DatahubContextProvider
                chartSvgs={chartSvgs}
                filters={{
                  toc,
                  city,
                  state,
                  roomType: 'one-bedroom',
                  include: 'neighborhoodSurvey,county,state',
                }}
              >
                <SearchPageInsightsWrapper geoGuide={geoGuide} />
              </DatahubContextProvider>
            )}

            {!isInternational && (
              <ExploreContainer
                filters={currentFilters}
                geoGuide={geoGuide}
                boxCtaProps={{
                  ctaProps:
                    ctaBoxAfterSearch ||
                    DEFAULT_CTA_OBJECTS.searchAssessmentWizard,
                  startLink: `/wizards/assessment/location/${state}/${city} `,
                  mode: { cta: 'generalOptions', entry: 'searchList' },
                  ctaButtonText,
                  ctaBoxDescription,
                  ctaBoxHeading,
                  ...getTypeformProps({
                    city,
                    state,
                    mode: { cta: 'generalOptions', entry: 'searchList' },
                  }),
                  ...getEventProps(
                    ctaBoxAfterSearch ||
                      DEFAULT_CTA_OBJECTS.searchAssessmentWizard,
                    {
                      location: 'search list',
                      converting: true,
                      adPosition: 100,
                      page,
                    }
                  ),
                }}
              />
            )}
          </div>
          {show === LIST &&
            pagination?.current === 1 &&
            geoGuide?.info?.curated &&
            !(Object.keys(currentFilters).length > 3) && (
              <CuratedIntro
                city={city}
                state={state}
                toc={toc}
                geoGuide={geoGuide}
                communitiesCount={pagination?.count}
              />
            )}

          {((breakpoint?.atLeastLaptop() && show === MAP) ||
            (breakpoint?.upToLaptop() && show === MAP)) && (
            <SearchMap
              headerHeight={headerHeight}
              filtersHeight={filtersHeight}
              show={show}
              breakpoint={breakpoint}
              query={query}
              meta={meta}
              entities={entities}
              onFilterChange={onFilterChange}
              hoveredCommunity={hoveredCommunity}
              cursor={cursor}
              currentFilters={currentFilters}
            />
          )}
        </div>
        <div
          className={cx(show === LIST ? 'block' : 'hidden')}
          data-tp-id='gen-div-a03fcf0a-8cbf-4e09-8421-c8aeae70b762'
        >
          <Footer startingWithLaptop='block' />
        </div>
      </div>
    </>
  );
};

export default Search;
